import React, { useCallback, useEffect, useState } from "react";
import { Modal, Select, Spin, Tooltip, Tag } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import IssueKeyBadge from "./IssueKeyBadge";
import * as issueLinksApi from "../../../../core/data/redux/services/issueLinksApi";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import { errMsg } from "../../helpers/errMsg.helper";
import { SETTINGS_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type { IssueLink } from "../../../../core/data/redux/projects/types";

interface LinksPanelProps {
  issueId: number;
  projectId: number;
  initialLinks?: Record<string, IssueLink[]>;
}

const LINK_TYPE_LABELS: Record<string, string> = {
  BLOCKS: "blocks",
  IS_BLOCKED_BY: "is blocked by",
  RELATES_TO: "relates to",
  DUPLICATES: "duplicates",
  IS_DUPLICATED_BY: "is duplicated by",
  CLONES: "clones",
  IS_CLONED_BY: "is cloned by",
};

const LINK_TYPE_OPTIONS = [
  { value: "BLOCKS", label: "blocks" },
  { value: "IS_BLOCKED_BY", label: "is blocked by" },
  { value: "RELATES_TO", label: "relates to" },
  { value: "DUPLICATES", label: "duplicates" },
  { value: "CLONES", label: "clones" },
];

const LinksPanel: React.FC<LinksPanelProps> = ({
  issueId,
  projectId,
  initialLinks = {},
}) => {
  const activeCompany = useSelector(selectActiveCompany);
  const [links, setLinks] = useState<Record<string, IssueLink[]>>(initialLinks);
  const [modalOpen, setModalOpen] = useState(false);
  const [linkType, setLinkType] = useState<string>("BLOCKS");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!activeCompany?.company_id) return;
    setLoading(true);
    try {
      const data = await issueLinksApi.listByIssue(
        activeCompany.company_id,
        activeCompany.company_type,
        issueId
      );
      setLinks(data?.links || {});
    } catch {
      setLinks({});
    } finally {
      setLoading(false);
    }
  }, [activeCompany, issueId]);

  useEffect(() => {
    if (Object.keys(initialLinks).length === 0) reload();
  }, [reload, initialLinks]);

  const runSearch = useCallback(
    async (value: string) => {
      if (!activeCompany?.company_id) return;
      setSearching(true);
      try {
        const data = await issuesApi.searchIssues(
          activeCompany.company_id,
          activeCompany.company_type,
          { project_ids: [projectId], q: value, limit: 20 }
        );
        setSearchResults(
          (data?.issues || []).filter((i: any) => i.id !== issueId)
        );
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to search issues"));
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [activeCompany, projectId, issueId]
  );

  useEffect(() => {
    if (modalOpen) {
      runSearch("");
    }
  }, [modalOpen, runSearch]);

  const handleCreateLink = async () => {
    if (!selectedIssueId) {
      notify.error("Select an issue to link");
      return;
    }
    setSubmitting(true);
    try {
      await issueLinksApi.createLink(
        activeCompany.company_id,
        activeCompany.company_type,
        {
          source_issue_id: issueId,
          target_issue_id: selectedIssueId,
          link_type: linkType,
        }
      );
      notify.success("Link created");
      setModalOpen(false);
      setSelectedIssueId(null);
      setSearchResults([]);
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to create link"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLink = async (id: number) => {
    try {
      await issueLinksApi.deleteLink(
        activeCompany.company_id,
        activeCompany.company_type,
        id
      );
      notify.success("Link removed");
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to delete link"));
    }
  };

  const allLinks = Object.entries(links);
  const totalLinks = allLinks.reduce((sum, [, arr]) => sum + arr.length, 0);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="small text-muted">
          {totalLinks} linked issue{totalLinks !== 1 ? "s" : ""}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-info"
          style={{ borderRadius: 6 }}
          onClick={() => setModalOpen(true)}
        >
          <i className="ti ti-link me-1" />
          Link Issue
        </button>
      </div>

      {loading && totalLinks === 0 ? (
        <div className="text-center py-2">
          <Spin size="small" />
        </div>
      ) : totalLinks === 0 ? (
        <p className="text-muted small mb-0">No linked issues.</p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {allLinks.map(([type, items]) => (
            <div key={type}>
              <div className="small fw-semibold text-muted mb-1">
                {LINK_TYPE_LABELS[type] || type}
              </div>
              <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                {items.map((link) => (
                  <li
                    key={link.id}
                    className="d-flex align-items-center gap-2 p-2"
                    style={{
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      fontSize: 13,
                    }}
                  >
                    {link.target_issue && (
                      <>
                        <IssueKeyBadge
                          projectKey={link.target_issue.issue_key.split("-")[0]}
                          issueKey={link.target_issue.issue_key}
                        />
                        <span className="flex-grow-1 text-truncate">
                          {link.target_issue.title}
                        </span>
                        <Tag color="default" style={{ margin: 0 }}>
                          {link.target_issue.type}
                        </Tag>
                      </>
                    )}
                    <Tooltip title="Remove link">
                      <button
                        type="button"
                        className="btn btn-sm btn-light ms-auto"
                        style={{ borderRadius: 6 }}
                        onClick={() => handleDeleteLink(link.id)}
                      >
                        <i className="ti ti-x" style={{ color: "#ef4444" }} />
                      </button>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Modal
        {...SETTINGS_MODAL_PROPS}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedIssueId(null);
          setSearchResults([]);
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "#3b82f618",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3b82f6",
                fontSize: 15,
              }}
            >
              <LinkOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Link Issue</div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "#94a3b8",
                  fontWeight: 400,
                }}
              >
                Connect this issue with another in the project
              </div>
            </div>
          </div>
        }
        footer={
          <div
            style={{
              padding: "14px 28px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button
              className="btn btn-light border px-4"
              style={{ borderRadius: 8 }}
              onClick={() => {
                setModalOpen(false);
                setSelectedIssueId(null);
                setSearchResults([]);
              }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="btn px-4"
              style={{
                borderRadius: 8,
                background: "#10b981",
                color: "#fff",
                fontWeight: 600,
              }}
              onClick={handleCreateLink}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Linking...
                </>
              ) : (
                <>
                  <LinkOutlined className="me-2" />
                  Link
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="mb-3">
          <label className="form-label small fw-semibold">Link Type</label>
          <Select
            className="w-100"
            value={linkType}
            onChange={setLinkType}
            options={LINK_TYPE_OPTIONS}
          />
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Search Issue</label>
          <Select
            showSearch
            className="w-100"
            placeholder="Type issue key or title..."
            filterOption={false}
            onSearch={runSearch}
            onChange={(v) => setSelectedIssueId(v)}
            value={selectedIssueId}
            loading={searching}
            notFoundContent={searching ? <Spin size="small" /> : "No results"}
            options={searchResults.map((i: any) => ({
              value: i.id,
              label: `${i.issue_key} — ${i.title}`,
            }))}
          />
        </div>
      </Modal>
    </>
  );
};

export default LinksPanel;
